import { Button, Group, Text } from "@mantine/core";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TableData, TableView } from "../../../custom-components/table";
import t from "../../../stores/language";
import theme from "../../../stores/theme";
import { useStore } from "../../../stores/store";
import { useStyles } from "../configure-input";
import ReportSave, { defaultReport, ReportSaveHandle } from "../reports/report-save";
import { getAssetPreview } from "./insurance-data";
import { downloadExcelSheet } from "./insurance-sheet";

const restorationPercentLabel = "Estimated Demolition / Disposal / Site Restoration %";
const restorationAmountLabel = "Estimated Demolition / Disposal / Site Restoration ($)";
const insuranceCostLabel = "Estimated Insurance Cost ($)";

const moneyNumber = (value: string | number | null) => {
  const cleaned = String(value ?? "").replace(/[$,\s]/g, "");
  if (!/^-?\d+(\.\d+)?$/.test(cleaned)) {
    return null;
  }
  const number = Number(cleaned);
  return Number.isFinite(number) ? number : null;
};

const percentNumber = (value: string | number | null) => {
  const cleaned = String(value ?? "")
    .replace(/%/g, "")
    .trim();
  if (!/^-?\d+(\.\d+)?$/.test(cleaned)) {
    return null;
  }
  const number = Number(cleaned);
  return Number.isFinite(number) ? number : null;
};

const formatMoney = (value: number) => {
  const [whole, fraction] = value.toFixed(2).split(".");
  const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `$${withCommas}.${fraction}`;
};

const withEstimates = (rows: TableData[]) =>
  rows
    .filter((row) => row.reason === "")
    .map((row) => {
      const cost = moneyNumber(row.replacementCost);
      const percent = percentNumber(row.restorationPercent);
      if (cost == null || percent == null) {
        return { ...row, restorationAmount: "", insuranceCost: "" };
      }
      const restorationAmount = cost * (percent / 100);
      return {
        ...row,
        restorationAmount: formatMoney(restorationAmount),
        insuranceCost: formatMoney(cost + restorationAmount),
      };
    });

const estimateColumns = [
  { key: "assetId", label: "Asset ID" },
  { key: "assetName", label: "Asset Name" },
  { key: "codeType", label: "Code Type" },
  { key: "replacementCost", label: "Replacement Cost ($)" },
  { key: "restorationPercent", label: restorationPercentLabel },
  { key: "restorationAmount", label: restorationAmountLabel },
  { key: "insuranceCost", label: insuranceCostLabel },
];

const InsuranceEstimates = () => {
  const { classes } = useStyles();
  const { store } = useStore();
  const navigateTo = useNavigate();
  const reportSaveRef = useRef<ReportSaveHandle>(null);
  const [saveError, setSaveError] = useState("");
  const [rows] = useState<TableData[]>(() => withEstimates(getAssetPreview()));

  const openSaveReport = () => {
    const insuranceModule = store.modules.find((module) => module.parentId === 0 && module.name.trim().toLowerCase() === "insurance");
    if (insuranceModule == null) {
      setSaveError(t("Insurance module was not found"));
      return;
    }
    setSaveError("");
    reportSaveRef.current?.show({
      ...defaultReport,
      moduleId: insuranceModule.id,
      selectedInputs: {},
      formulasResult: {
        detailedResult: [],
        calculatedResult: rows.map((row) => Object.fromEntries(estimateColumns.map((column) => [column.label, row[column.key] ?? ""]))),
      },
    });
  };

  const downloadSheet = () => {
    const sheetRows = [estimateColumns.map((column) => column.label), ...rows.map((row) => estimateColumns.map((column) => String(row[column.key] ?? "")))];
    downloadExcelSheet("Calculated Estimates.xlsx", "Calculated Estimates", sheetRows);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 142px)" }}>
      <div className={classes.reportsContainer} style={{ flex: 1, minHeight: 0, height: "auto", display: "flex", flexDirection: "column" }}>
        <div className={classes.title}>
          <span>{t("Calculated Estimates")}</span>
        </div>
        <div style={{ flex: 1, minHeight: 0 }}>
          <TableView
            data={rows}
            columns={estimateColumns.map((column) => ({
              ...column,
              width: "14%",
              dataAlign: column.key === "assetId" || column.key === "assetName" || column.key === "codeType" ? "left" : "right",
            }))}
            fixedLayout
            height="100%"
          />
        </div>
      </div>
      <Group position="center" mt={10} style={{ flexShrink: 0 }}>
        <Button variant="default" onClick={() => navigateTo("/insurance/preview")}>
          {t("Back")}
        </Button>
        <Button variant="light" color="blue" onClick={downloadSheet}>
          {t("Download Sheet")}
        </Button>
        <Button disabled={rows.length === 0} onClick={openSaveReport}>
          {t("Save Report")}
        </Button>
      </Group>
      {saveError !== "" ? (
        <Text align="center" size={theme.fontSizes?.sm} color="red">
          {saveError}
        </Text>
      ) : null}
      <ReportSave ref={reportSaveRef} />
    </div>
  );
};

export default InsuranceEstimates;
